import { ActivityEntityName, Payment, PaymentSource, PaymentStatus, PaymentType } from '@jms/model';

import { defineConditionalTag } from '../../shared/define.conditional.tag';
import { defineStatusTag } from '../../shared/define.status.tag';

// Single source of Payment pill colour. Each pill feeds BOTH the manifest's board-card tag style and
// the Detail header's tag deriver, so a payment cannot read one colour on the card and another in the
// preview header.

/** Processing result. Every error variant reads the same to the user — the distinction is in the note. */
export const paymentStatusTag = defineStatusTag<PaymentStatus, Payment>({
  [PaymentStatus.Pending]: 'warning',
  [PaymentStatus.Success]: 'success',
  [PaymentStatus.Error]: 'error',
  [PaymentStatus.IntegrationError]: 'error',
  [PaymentStatus.WorkBuddyError]: 'error',
}, 'neutral');

/**
 * Money direction. A plain receivable or payable payment is the unremarkable case and shows no pill —
 * only a refund, credit allocation, prepayment or overpayment earns one, because those change how the
 * amount reads.
 */
export const paymentTypeTag = defineConditionalTag<Payment>(payment => {

  // A document's Payments list carries its credit allocations as payment-shaped rows marked by `data`.
  if (payment?.data === ActivityEntityName.CreditAllocation) return { label: 'Credit allocation', tone: 'warning' };
  switch (payment?.type) {
    case PaymentType.ReceivableCredit:
    case PaymentType.PayableCredit:
      return { label: 'Refund', tone: 'warning' };
    case PaymentType.ReceivableCreditAllocation:
    case PaymentType.PayableCreditAllocation:
      return { label: 'Credit allocation', tone: 'warning' };
    case PaymentType.ReceivablePrepayment:
    case PaymentType.PayablePrepayment:
      return { label: 'Prepayment', tone: 'brand' };
    case PaymentType.ReceivableOverpayment:
    case PaymentType.PayableOverpayment:
      return { label: 'Overpayment', tone: 'brand' };
    default:
      return null;
  }
});

/**
 * Accounting integration result, as the legacy payments grid's Accounting column decides it. Silent until the
 * payment has actually reached accounting: one never pushed has no result yet, and a payment that failed
 * itself (Error) never went. A credit allocation synced from the ledger may have no integration id, so its
 * accounting system and success stand in. The integration note explains a failure on hover.
 */
export const paymentAccountingTag = defineConditionalTag<Payment>(payment => {

  if (!payment || payment.status === PaymentStatus.Error) return null;
  const allocationSynced = payment.data === ActivityEntityName.CreditAllocation && !!payment.accountingSystem && payment.status === PaymentStatus.Success;
  if (!payment.integrationId && payment.status !== PaymentStatus.IntegrationError && !allocationSynced) return null;
  switch (payment.status) {
    case PaymentStatus.IntegrationError:
      return { label: 'Accounting error', tone: 'error', ...(payment.note ? { tooltip: payment.note } : {}) };
    // The ledger took more than the document owed.
    case PaymentStatus.WorkBuddyError:
      return { label: 'Over paid', tone: 'error', ...(payment.note ? { tooltip: payment.note } : {}) };
    default:
      return { label: 'Synced', tone: 'success' };
  }
});

/** How a Payment row in a document's list reports itself: a quiet icon when healthy, an inline error when it failed. */
export type PaymentRowHealth =
  | { readonly kind: 'icon'; readonly icon: string; readonly tone: 'success' | 'warning'; readonly tooltip: string }
  | { readonly kind: 'failed'; readonly reason: 'accounting' | 'stripe' | 'payment' | 'overpaid'; readonly title: string; readonly note?: string };

/**
 * Row health for the document Payments list, from the same facts as the board's status and accounting pills.
 * A failure is spelled out with its note (the legacy grid hid it behind a red logo); a healthy row only nods.
 * Nothing to report returns null. Board and preview keep their pills.
 */
export function paymentRowHealth(payment: Payment | null | undefined): PaymentRowHealth | null {

  if (!payment) return null;
  const note = payment.note?.trim();
  const failed = (reason: 'accounting' | 'stripe' | 'payment', title: string): PaymentRowHealth => ({ kind: 'failed', reason, title, ...(note ? { note } : {}) });
  switch (payment.status) {
    case PaymentStatus.Error:
      return payment.source === PaymentSource.Stripe ? failed('stripe', 'Stripe charge failed') : failed('payment', 'Payment failed');
    case PaymentStatus.IntegrationError:
      return failed('accounting', 'Accounting sync failed');
    case PaymentStatus.WorkBuddyError:
      return { kind: 'failed', reason: 'overpaid', title: 'Over paid — exceeds the outstanding amount' };
    case PaymentStatus.Pending:
      return { kind: 'icon', icon: 'fa-regular fa-clock', tone: 'warning', tooltip: 'Pending' };
    default:
      return paymentAccountingTag(null, payment)
        ? { kind: 'icon', icon: 'fa-regular fa-circle-check', tone: 'success', tooltip: 'Synced to accounting' }
        : null;
  }
}
